import * as three from 'three';

import { Component, h, primitiveNodeTypes, Ref, useEffect, useMemo, useRef, useState } from "@lukekaalim/act";
import { html } from "@lukekaalim/act-web";
import { a3, PropsFromClass, ThreeJSRoot } from "@lukekaalim/act-three";

import { DrivableCamera, graphicsSetupContext, inputBeat, renderBeat, StarField, StarFieldMaterial, useDisposable, useGraphics3DSetup } from "@kosmod/graphics-3d";
import { ClientData, createJanitor, cyrb64, ID, Map2D, stringHash, SubjectData, useAsync, useBeat, useClientData, useIsDebug, useKeyboardInput, useLogger, useMappedClientData, useNullableMappedClientData } from "@kosmod/kit";

import { TheatreCoreResources } from "../hooks/useTheatreCoreResources";
import { TheatreState, TheatreStateContext, useTheatreState } from '../hooks/useTheatreState';
import { TheatreController, TheatreData } from '../hooks/useTheatreController';
import { ToolPreview } from './Tools/ToolRenderer';
import { getAdjacent, GridChunkRenderer } from './GridChunkRenderer';
import { MiniClientRenderer, MiniRenderer } from './Theatre/Mini';
import { SimpleTerrainSystemRenderer } from './TerrainSystems/SimpleTerrainSystem';
import { Mini, MiniID, PhysicsDice, TerrainCanvas, theatreAPI } from '../defs';

import classes from './Theatre.module.css';
import { DeadSimpleTerrainSystem } from './TerrainSystems/DeadSimpleTerrain';
import { createSpace2DFromChunkMap } from '../lib/space';
import { DOISTerrain } from './TerrainSystems/DOISTerrain';
import { PlayerCursor } from './Theatre/PlayerCursor';
import { SurfaceRenderer } from './Theatre/Surface';
import { ConciergeState } from '@kosmod/concierge';
import { TheatreOverlay } from './TheatreOverlay';
import { DebugReadout } from './DebugReadout';
import { HttpApi, NetworkClient } from '@kosmod/pancake';
import { assetsAPI, GameAssetClient } from '@kosmod/assets';
import { DragPreview } from './TheatreOverlay/ContentBrowser/DragPreview';
import { PhysicsDieRenderer, RemotePhysicsDie } from './Theatre/PhysicsDie';
import { usePhysics } from '../hooks/usePhysics';
import { useHostSync } from '../hooks/useHostSync';
import { TheatreSceneResources } from '../hooks/useTheatreSceneResources';

export type TheatreProps = {
  res: TheatreCoreResources,
  sceneRes: TheatreSceneResources,

  concierge: ConciergeState,

  clients: {
    net: NetworkClient,
    theatre: HttpApi.Handler<"client", typeof theatreAPI>,
    assets: GameAssetClient,
  },
  controller: TheatreController,
  data: TheatreData
};


export const Theatre: Component<TheatreProps> = ({ res, sceneRes, controller, data, concierge, clients }) => {
  const ref = useRef<HTMLElement | null>(null);

  const onRender = useBeat(renderBeat)
  
  const starFieldRef = useRef<null | StarField>(null);

  const state = useTheatreState(concierge, ref, controller, data, res, sceneRes, clients);

  useEffect(() => {
    const starfield = starFieldRef.get();
    const camera = state.camera.get();
    if (!starfield || !camera)
      return;

    const cancel = onRender((now) => {
      const { x, z } = camera.targetPosition;
      starfield.position.set(x, starfield.position.y, z);

      starfield.material.origin.set((x / 4) + (now * 0.00004), starfield.position.y, (z / 4));
    });

    return cancel;
  }, [])

  const logger = useLogger('Theatre');
  const activeTool = state.tools.allTools.find(t => t.id === state.tools.activeTool)

  return h(html.div, { ref, tabIndex: 0, autofocus: true, className: classes.theater },
    h(TheatreStateContext.Provider, { value: state },
      h(graphicsSetupContext.Provider, { value: state.graphics }, [
        h(html.canvas, {
          ref: state.graphics.canvas,
          style: { width: '100%', height: '100%' },
        }),
        h(primitiveNodeTypes.null, {}, h(ThreeJSRoot, {}, [
          h(a3.scene, { ref: state.graphics.scene, background: new three.Color('black') }, [
            h(ToolPreview, { state }),
            h(DrivableCamera, {
              fov: 40, ref: state.camera, aspect: 16/9,
              keyboard: state.viewMode === 'scene' && state.keyboard || null
            }),
            data.ready && [
              data.surfaces.map(surface => {
                return h(SurfaceRenderer, {
                  surfaceId: surface.id,
                  theatre: state,
                  canvases: state.data.canvases,
                });
              }),
              (!activeTool || activeTool.type !== 'dev:vis') && data.minis.map((mini) => h(MiniClientRenderer, {
                key: mini.current.id,
                state,
                client: mini,
              })),
              data.players.map(player => h(PlayerCursor, {
                id: player.id, tool: player.tool, cursor: player.cursor, theatre: state
              })),
              data.ownedPhysicsDice.map(die => {
                return state.physics && h(PhysicsDieRenderer, {
                  key: die.id,
                  die, simulation: state.physics,
                  resources: state.sceneRes, data, diceMap: state.localDiceTransforms
                })
              }),
              data.remotePhysicsDice.map(die => {
                return h(RemotePhysicsDie, { key: die.id, die, res: state.sceneRes, data: data })
              }),
            ],
            h(a3.directionalLight, { position: { x: 0 , y: 10, z: 0 }}),
            h(a3.directionalLight, { position: { x: 10 , y: 10, z: 5 }}),
            h(a3.group, { name: 'Skybox' }, [
              h(StarField, {
                ref: starFieldRef,
                height: 15,
                size: 8,
                count: 1024 * 32,
                volume: 40,
                color: new three.Color('#d0ecee'),
                flickerSpeed: 0.2,
                falloff: 5,
                waveHeight: 0.05,
                waveSpeed: 1,
                position: { x: 0, y: -15, z: 0 },
                texture: res.starDiamond
              }),
            ])
          ]),
        ])),
        h(DebugReadout, { state }),
        h(html.div, { ref: state.containerRef, style: { position: 'absolute' } }),
        h(TheatreOverlay, { state }),
        state.selection.drag && h(DragPreview, { target: state.selection.drag.subject, state })
      ]),
    ),
  );
}   

export * from './Theatre/Mini';
export * from './Theatre/Flag';
